import type { Product } from '../types/order';
import { productDisplayPrice, sizeChannelPrice } from './pricing';

/**
 * Τιμές ΕΜΦΑΝΙΣΗΣ στις οθόνες διαχείρισης — προσφορές, αντιστοίχιση πλατφορμών, φύρα,
 * αγαπημένα πελάτη.
 *
 * Φ5: «η τιμή ζει στο μέγεθος» (docs/base_price_phase5_plan.md §3). Καμία στήλη τιμής
 * προϊόντος δεν διαβάζεται εδώ· η τιμή έρχεται από τον server (display_price / unit_price) ή
 * από το productDisplayPrice / sizeChannelPrice του pricing.ts, ώστε η οθόνη να λέει ό,τι λέει
 * και το πλακίδιο του ταμείου.
 *
 * ΚΑΝΕΝΑ ποσό παραγγελίας/απόδειξης δεν βγαίνει από εδώ — μόνο κείμενο οθόνης.
 */

/** Ευρώ → ακέραια λεπτά, για να μη γίνεται άθροισμα float (0,1 + 0,2). */
const toCents = (v: unknown): number => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.round(n * 100) : 0;
};

/** Τιμή του server (NUMERIC ως string ή αριθμός)· null αν λείπει ή δεν είναι αριθμός. */
const serverPrice = (v: unknown): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const euro = (n: number) => `€${n.toFixed(2)}`;

/* ───────────────────────── Προσφορές ───────────────────────── */

/**
 * Πραγματικό % μιας έκπτωσης σταθερού ποσού πάνω στην τιμή του προϊόντος.
 *
 * Τιμή 0 (ή άγνωστη) → null = «δεν ξέρουμε», όχι διαίρεση με 1: αλλιώς 0,50 € σε προϊόν
 * χωρίς τιμή θα έβγαινε 50% και κάθε τέτοιο προϊόν θα «παραβίαζε» το όριο έκπτωσης.
 */
export function effectiveFixedDiscountPercent(price: number, amount: number): number | null {
  const p = Number(price);
  if (!(Number.isFinite(p) && p > 0)) return null;
  return (Number(amount) / p) * 100;
}

/* ───────────────────────── Αντιστοίχιση πλατφορμών ───────────────────────── */

/** Ό,τι στέλνει το GET /product-mappings/:storeId/internal-products. */
export type MappingSize = {
  id: number;
  name: string;
  price_modifier: number | string;
  price_modifier_take_away?: number | string | null;
  price_modifier_delivery?: number | string | null;
  price_modifier_dine_in?: number | string | null;
  is_default?: boolean | null;
  is_available?: boolean | null;
  is_hidden?: boolean | null;
  display_order?: number | null;
};

export type MappingProduct = {
  id: number;
  display_price?: number | string | null;
  fee_category?: number | null;
  fee_unit_gross?: number | string | null;
  sizes?: MappingSize[];
};

/**
 * Τιμή του τοπικού προϊόντος στη λίστα αντιστοίχισης (κανάλι delivery — οι πλατφόρμες
 * είναι delivery): το display_price του server (displayPriceSql). Αν λείπει (προϊόν χωρίς
 * μέγεθος, τέλος χωρίς state_fees), το productDisplayPrice δίνει την ίδια απάντηση με το
 * ταμείο — 0 για την ανωμαλία, όχι NaN.
 */
export function mappingProductPrice(ip: MappingProduct): number {
  return serverPrice(ip.display_price) ?? productDisplayPrice(ip as unknown as Product, 'delivery');
}

export function mappingProductSublabel(ip: MappingProduct): string {
  return euro(mappingProductPrice(ip));
}

/**
 * Ετικέτα μεγέθους στο dropdown αντιστοίχισης επιλογής → μέγεθος: η ΑΠΟΛΥΤΗ τιμή delivery
 * του μεγέθους, χωρίς «+» — «ΚΑΝΟΝΙΚΟ (€1.40)». Το σκέτο price_modifier θα έλεγε ψέματα όταν
 * το μέγεθος έχει δική του τιμή delivery.
 */
export function mappingSizeLabel(size: MappingSize): string {
  return `${size.name} (${euro(sizeChannelPrice(size as Parameters<typeof sizeChannelPrice>[0], 'delivery'))})`;
}

/* ───────────────────────── Φύρα ───────────────────────── */

/**
 * ΕΚΤΙΜΗΣΗ αξίας φύρας για την οθόνη (τιμή take-away του πλακιδίου × ποσότητα).
 * Η αξία που ΑΠΟΘΗΚΕΥΕΤΑΙ τη βγάζει ο server (utils/sellingPrice.js) — μπορεί να
 * διαφέρει (π.χ. ζυγιζόμενα)· γι' αυτό η οθόνη δεν τη λέει «αποθηκευμένη».
 * Άθροισμα σε λεπτά.
 */
export function wasteEstimateCents(product: Product, quantity: number): number {
  return toCents(productDisplayPrice(product, 'take_away')) * (Number(quantity) || 0);
}

export function wasteEstimateTotal(lines: Array<{ product: Product; quantity: number }>): number {
  return lines.reduce((s, l) => s + wasteEstimateCents(l.product, l.quantity), 0) / 100;
}

/* ───────────────────────── Αγαπημένα πελάτη ───────────────────────── */

export type FavoriteLike = {
  unit_price?: number | string | null;
};

/**
 * Τιμή αγαπημένου: το unit_price του server (τιμή delivery της γραμμής). Λείπει μόνο για
 * ανωμαλία (προϊόν χωρίς μέγεθος, τέλος χωρίς state_fees) → 0, όπως το κατώτατο του server.
 */
export function favoriteUnitPrice(item: FavoriteLike): number {
  return serverPrice(item.unit_price) ?? 0;
}
